import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AIChatbot from '../components/AIChatbot';
import PhoneField, { isPhoneValid } from '../components/PhoneField';
import { galleryWhatsAppLink } from '../lib/whatsapp';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isPhoneValid(formData.phone)) {
      setError('Please enter a valid phone number with country code.');
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `Subject: ${formData.subject}\n\n${formData.message}`,
          inquiry_type: 'contact_form',
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Request failed (${res.status})`);
      }
      setStatus('success');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setTimeout(() => setStatus(''), 4000);
    } catch (err) {
      setError(err.message || 'Could not send your message. Please try again.');
      setStatus('');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const waLink = galleryWhatsAppLink('Hello Zebra One Gallery, I have an enquiry…');

  const contactInfo = [
    {
      icon: MapPin,
      title: "Visit Us",
      content: ["Perrins Court", "73a Hampstead High Street", "London NW3 1QH", "United Kingdom"]
    },
    {
      icon: Phone,
      title: "Call Us",
      content: ["+44 (0) 20 7794 1281"],
      link: "tel:02077941281"
    },
    {
      icon: Mail,
      title: "Email Us",
      content: ["info@zebraonegallery.com"],
      link: "mailto:info@zebraonegallery.com"
    },
    ...(waLink ? [{
      icon: MessageCircle,
      title: "WhatsApp",
      content: ["Message us directly", "Replies usually within the hour"],
      link: waLink,
      external: true,
    }] : []),
    {
      icon: Clock,
      title: "Opening Hours",
      content: ["Tuesday - Saturday: 10am - 5pm", "Sunday - Monday: By Appointment"]
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-6 font-sans">
              Get in Touch
            </p>
            
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl text-foreground mb-8 leading-[1.1]">
              Contact Us
            </h1>
            
            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl font-sans leading-relaxed">
              Whether you're looking to acquire art, need framing services, or want to discuss 
              a consultation, we'd love to hear from you. Visit us in Hampstead or get in touch below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Grid */}
      <section className="py-20 px-6 bg-muted/20">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <motion.div
                  key={info.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-background p-8 border border-border"
                >
                  <Icon className="w-8 h-8 mb-6 text-foreground" />
                  
                  <h3 className="font-serif text-xl text-foreground mb-4">
                    {info.title}
                  </h3>
                  
                  {info.link ? (
                    <a
                      href={info.link}
                      target={info.external ? "_blank" : undefined}
                      rel={info.external ? "noopener noreferrer" : undefined}
                      data-testid={info.title === 'WhatsApp' ? 'contact-whatsapp-link' : undefined}
                      className="text-muted-foreground font-sans hover:text-foreground transition-colors"
                    >
                      {info.content.map((line, i) => (
                        <p key={`${info.title}-line-${i}-${line.slice(0, 20)}`} className="leading-relaxed">{line}</p>
                      ))}
                    </a>
                  ) : (
                    <div className="text-muted-foreground font-sans">
                      {info.content.map((line, i) => (
                        <p key={`${info.title}-line-${i}-${line.slice(0, 20)}`} className="leading-relaxed">{line}</p>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 px-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                Send us a Message
              </h2>
              
              <p className="text-lg text-muted-foreground font-sans mb-8 leading-relaxed">
                For best results with framing services, we recommend visiting us in person. 
                For all other enquiries, please use the form below.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm uppercase tracking-wider text-foreground mb-2 font-sans">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-border bg-background text-foreground font-sans focus:outline-none focus:border-foreground transition-colors"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm uppercase tracking-wider text-foreground mb-2 font-sans">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-border bg-background text-foreground font-sans focus:outline-none focus:border-foreground transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-sm uppercase tracking-wider text-foreground mb-2 font-sans">
                      Phone *
                    </label>
                    <PhoneField
                      value={formData.phone}
                      onChange={(v) => setFormData({ ...formData, phone: v || '' })}
                      required
                      placeholder="Phone number"
                      testIdPrefix="contact-phone"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-foreground mb-2 font-sans">
                    Subject *
                  </label>
                  <select
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-border bg-background text-foreground font-sans focus:outline-none focus:border-foreground transition-colors"
                  >
                    <option value="">Select a subject</option>
                    <option value="general">General Enquiry</option>
                    <option value="artwork">Artwork Purchase</option>
                    <option value="framing">Picture Framing</option>
                    <option value="consultation">Art Consultation</option>
                    <option value="artist">Artist Representation</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm uppercase tracking-wider text-foreground mb-2 font-sans">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="6"
                    className="w-full px-4 py-3 border border-border bg-background text-foreground font-sans focus:outline-none focus:border-foreground transition-colors resize-none"
                  ></textarea>
                </div>

                <motion.button
                  type="submit"
                  disabled={status === 'sending'}
                  whileHover={{ scale: status === 'sending' ? 1 : 1.02 }}
                  whileTap={{ scale: status === 'sending' ? 1 : 0.98 }}
                  className="w-full px-6 py-3 bg-foreground text-background font-sans text-sm uppercase tracking-[0.2em] flex items-center justify-center gap-3 disabled:opacity-50"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                  <Send className="w-4 h-4" />
                </motion.button>

                {error && (
                  <p className="text-red-600 font-sans text-sm text-center" data-testid="contact-error">
                    {error}
                  </p>
                )}

                {status === 'success' && (
                  <motion.p
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-green-600 font-sans text-sm text-center"
                  >
                    ✓ Thank you! Your message has been sent successfully.
                  </motion.p>
                )}
              </form>
            </motion.div>

            {/* Map & Directions */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8"
            >
              <div>
                <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-6">
                  Visit the Gallery
                </h2>
                
                <p className="text-lg text-muted-foreground font-sans mb-8 leading-relaxed">
                  Located in the heart of Hampstead village, our gallery is easily accessible 
                  by tube, bus, or car. We're just a short walk from Hampstead Underground Station.
                </p>

                <div className="space-y-6">
                  <div className="border-l-4 border-foreground pl-6">
                    <h3 className="font-serif text-xl text-foreground mb-2">By Tube</h3>
                    <p className="text-muted-foreground font-sans">
                      Hampstead Station (Northern Line)<br />
                      5 minute walk via Hampstead High Street
                    </p>
                  </div>

                  <div className="border-l-4 border-foreground pl-6">
                    <h3 className="font-serif text-xl text-foreground mb-2">By Bus</h3>
                    <p className="text-muted-foreground font-sans">
                      Routes 46, 268 stop on Hampstead High Street<br />
                      Directly outside Perrins Court
                    </p>
                  </div>

                  <div className="border-l-4 border-foreground pl-6">
                    <h3 className="font-serif text-xl text-foreground mb-2">Parking</h3>
                    <p className="text-muted-foreground font-sans">
                      On-street parking available<br />
                      Heath Street Car Park (5 min walk)
                    </p>
                  </div>
                </div>
              </div>

              {/* Map Placeholder */}
              <div className="aspect-square bg-muted/30 border border-border flex items-center justify-center">
                <div className="text-center p-8">
                  <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-sm uppercase tracking-wider text-muted-foreground font-sans mb-2">
                    Perrins Court
                  </p>
                  <p className="text-xs text-muted-foreground font-sans">
                    73a Hampstead High Street<br />
                    London NW3 1QH
                  </p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Zebra+One+Gallery+Hampstead" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block mt-4 text-sm text-foreground underline hover:no-underline font-sans"
                  >
                    Open in Google Maps
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Hours & Additional Info */}
      <section className="py-20 px-6 bg-muted/20">
        <div className="max-w-[1400px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl text-foreground mb-12">
              We Look Forward to Seeing You
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-background p-8 border border-border">
                <h3 className="font-serif text-xl text-foreground mb-4">Gallery Viewing</h3>
                <p className="text-muted-foreground font-sans leading-relaxed">
                  Walk-ins welcome during opening hours. Private viewings available by appointment.
                </p>
              </div>

              <div className="bg-background p-8 border border-border">
                <h3 className="font-serif text-xl text-foreground mb-4">Framing Consultations</h3>
                <p className="text-muted-foreground font-sans leading-relaxed">
                  Bring your artwork for expert advice and quotes. No appointment necessary.
                </p>
              </div>

              <div className="bg-background p-8 border border-border">
                <h3 className="font-serif text-xl text-foreground mb-4">Art Consultation</h3>
                <p className="text-muted-foreground font-sans leading-relaxed">
                  Book a private consultation to discuss sourcing, selling, or curating art.
                </p>
              </div>
            </div>

            <p className="text-sm text-muted-foreground font-sans">
              <strong className="text-foreground">Zebra One Gallery</strong> · Established 1976 · Hampstead, London
            </p>
          </motion.div>
        </div>
      </section>

      {/* AI Chatbot Widget */}
      <AIChatbot />

      <Footer />
    </div>
  );
};

export default ContactPage;
